/**
 * The activities of one deal, person or organization: what is planned, with a
 * checkbox to mark each done, and the last few that were done.
 *
 * Used on all three detail screens. "Add activity" starts the form linked to
 * the record the panel belongs to; on a deal, the server copies the deal's
 * person and organization onto it.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { CRM_PERMISSIONS, type ActivityDto, type CrmRefDto } from '@forsys/shared';
import { useAuth } from '../../auth/useAuth';
import { AsyncContent } from '../../components/AsyncContent';
import { ErrorState } from '../../components/ErrorState';
import { Panel } from '../../components/Panel';
import { RowActions } from '../../components/RowActions';
import { useAsync } from '../../hooks/useAsync';
import { useActivityActions, withoutDone } from './actions';
import { ActivityDoneCheckbox } from './ActivityDoneCheckbox';
import { ActivityIcon } from './ActivityIcon';
import { listActivities, today } from './api';
import { dueState, formatDay } from './format';
import { NewActivityDialog } from './NewActivityDialog';

export interface ActivityPanelProps {
  /** Which record's activities: exactly one of these. */
  filter: { dealId: number } | { personId: number } | { organizationId: number };
  /** What a new activity is linked to. */
  link: { deal?: CrmRefDto; person?: CrmRefDto; organization?: CrmRefDto };
  /** After anything here changed — a deal's next activity may have, too. */
  onChanged?: () => void;
}

const PLANNED = 50;
const RECENTLY_DONE = 10;

export function ActivityPanel({ filter, link, onChanged }: ActivityPanelProps) {
  const { t } = useTranslation('crm');
  const { can } = useAuth();
  const day = today();
  const key = JSON.stringify(filter);

  const planned = useAsync(() => listActivities({ ...filter, due: 'open', limit: PLANNED }), [key]);
  const done = useAsync(
    () => listActivities({ ...filter, due: 'done', sort: 'dueDate', order: 'desc', limit: RECENTLY_DONE }),
    [key],
  );

  const changed = () => {
    planned.reload();
    done.reload();
    onChanged?.();
  };

  const actions = useActivityActions({ onChanged: changed });
  const [creating, setCreating] = useState(false);

  function row(activity: ActivityDto) {
    const isDone = activity.doneAt !== null;
    const state = isDone ? null : dueState(activity.dueDate, day);
    const links = [
      'dealId' in filter ? null : activity.deal && { to: `/crm/deals/${activity.deal.id}`, name: activity.deal.name },
      'personId' in filter ? null : activity.person && { to: `/crm/persons/${activity.person.id}`, name: activity.person.name },
    ].filter((one): one is { to: string; name: string } => Boolean(one));

    return (
      <Stack key={activity.id} direction="row" spacing={1} sx={{ alignItems: 'center', py: 0.5 }}>
        <ActivityDoneCheckbox activity={activity} rowActions={actions.rowActions} />
        <ActivityIcon type={activity.type} color="action" />
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography
            variant="body2"
            sx={{ textDecoration: isDone ? 'line-through' : 'none', overflowWrap: 'anywhere' }}
          >
            {activity.subject}
          </Typography>
          <Typography
            variant="caption"
            color={state === 'overdue' ? 'error' : 'text.secondary'}
            component="div"
          >
            {formatDay(activity.dueDate)}
            {activity.dueTime ? ` ${activity.dueTime}` : ''} · {activity.owner.name}
            {links.map((one) => (
              <span key={one.to}>
                {' · '}
                <Link component={RouterLink} to={one.to} underline="hover" color="inherit">
                  {one.name}
                </Link>
              </span>
            ))}
          </Typography>
        </Box>
        <RowActions actions={withoutDone(actions.rowActions(activity))} />
      </Stack>
    );
  }

  return (
    <Panel
      title={t('activity.panel.title')}
      actions={
        can(CRM_PERMISSIONS.WRITE) && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
            {t('activity.new')}
          </Button>
        )
      }
    >

      {actions.error && <ErrorState error={actions.error} />}

      <AsyncContent state={planned}>
        {({ activities }) =>
          activities.length === 0 ? (
            <Typography variant="body2" color="warning.main" sx={{ py: 1 }}>
              {'dealId' in filter ? t('activity.panel.nothingPlannedDeal') : t('activity.panel.nothingPlanned')}
            </Typography>
          ) : (
            <Box>{activities.map(row)}</Box>
          )
        }
      </AsyncContent>

      <AsyncContent state={done}>
        {({ activities }) =>
          activities.length === 0 ? null : (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="overline" color="text.secondary">
                {t('activity.panel.recentlyDone')}
              </Typography>
              <Box>{activities.map(row)}</Box>
            </>
          )
        }
      </AsyncContent>

      {actions.dialogs}

      <NewActivityDialog
        open={creating}
        link={link}
        onClose={() => setCreating(false)}
        onCreated={changed}
      />
    </Panel>
  );
}
